import { useState } from "react";
import { useIntl } from "react-intl";
import { confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import Section from "../../../common/section";
import TooltipButton from "../../../common/tooltipButton";
import UserService from "../../../../services/UserService";
import { errorText } from "../../../../helpers/ErrorMessage";

// The user's authenticator app: whether it is set up and since when, with
// Reset MFA for a lost phone (the user sets it up again at their next
// sign-in). Shown while it is set up or while the organisation requires MFA.
// A SysRoot user's authenticator is reset only by a SysRoot user (sysRootOnly
// is true for anyone else).
const UserMfaCard = ({ record, canUpdate, sysRootOnly = false, onReset }) => {
  const intl = useIntl();
  const [resetting, setResetting] = useState(false);
  const name = `${record?.firstName || ""} ${record?.lastName || ""}`.trim();

  const reset = async () => {
    setResetting(true);
    try {
      await UserService.resetMfa({ id: record.id });
      toast.success(intl.formatMessage({ id: "reset_mfa_done" }));
      onReset();
    } catch (error) {
      toast.error(
        errorText(error?.response?.data, intl, "something_else_wrong")
      );
    } finally {
      setResetting(false);
    }
  };

  const enrolled = Boolean(record?.hasMfa);
  const status = enrolled
    ? record?.mfaConfirmedAt
      ? intl.formatMessage(
          { id: "mfa_set_up_on" },
          {
            date: intl.formatDate(record.mfaConfirmedAt, {
              dateStyle: "medium",
            }),
          }
        )
      : intl.formatMessage({ id: "mfa_set_up" })
    : intl.formatMessage({ id: "mfa_not_set_up" });

  const blockedBy = !canUpdate
    ? "no_permission"
    : sysRootOnly
    ? "sysroot_only"
    : null;

  const confirmReset = () => {
    confirmDialog({
      header: intl.formatMessage({ id: "reset_mfa" }),
      message: intl.formatMessage(
        { id: "reset_mfa_confirm_message" },
        { name }
      ),
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptLabel: intl.formatMessage({ id: "reset_mfa" }),
      rejectLabel: intl.formatMessage({ id: "cancel" }),
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-secondary",
      accept: reset,
      reject: () => {},
    });
  };

  return (
    <div
      className={`edexlms-details-card ${
        enrolled ? "completed" : "incompleted"
      }`}
      style={{ flex: 1 }}
    >
      <Section
        title={intl.formatMessage({ id: "mfa_status" })}
        i18n={false}
        headerAction={
          enrolled && (
            <TooltipButton
              type="button"
              outlined
              severity="danger"
              size="small"
              icon="pi pi-refresh"
              label={intl.formatMessage({ id: "reset_mfa" })}
              onClick={confirmReset}
              disabled={!!blockedBy || resetting}
              tooltipOptions={{ position: "bottom" }}
              tooltip={blockedBy ? intl.formatMessage({ id: blockedBy }) : ""}
            />
          )
        }
      >
        <div className="grid gap-4">
          <div className="col-12">
            <div className="edexlms-field">
              <div className="edexlms-field-label disabled">
                {intl.formatMessage({ id: "status" })}
              </div>
              <div data-testid="mfa-status">{status}</div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default UserMfaCard;
